<script lang="ts">
	import type { ComponentProps, Snippet } from 'svelte';
	import HelperText from '$lib/components/atoms/helper-text/HelperText.svelte';

	interface Props {
		id: string;
		text?: string;
		checked?: boolean;
		disabled?: boolean;
		uncheckedTrackClass?: string;
		helperTextProps?: ComponentProps<typeof HelperText>;
		children?: Snippet;
		onchange?: (event: Event) => void;
	}

	let {
		id,
		text,
		checked = $bindable(false),
		disabled = false,
		uncheckedTrackClass = 'bg-neutral',
		helperTextProps,
		children,
		onchange,
	}: Props = $props();

	const trackClasses = $derived.by(() => {
		if (disabled) {
			return 'bg-neutral-soft';
		}

		if (checked) {
			return 'bg-coloredneutral';
		}

		return uncheckedTrackClass;
	});

	const thumbClasses = $derived.by(() => {
		const shadowEnabled = checked && !disabled ? 'shadow-control-on' : '';

		if (checked) {
			return `translate-x-full ${shadowEnabled}`;
		}

		return 'translate-x-none shadow-control-off';
	});
</script>

<label for={id} class={['flex items-start gap-2 max-w-max p-0.5', disabled
	? 'text-neutral-soft element-disabled'
	: 'text-coloredneutral cursor-pointer']}>
	<input
		type="checkbox"
		{id}
		role="switch"
		class="sr-only"
		bind:checked
		{disabled}
		{onchange}
		aria-describedby={helperTextProps?.id || null}
	/>
	<span
		class={['flex flex-grow-0 rounded-full min-w-9 w-9 h-5 p-0.5 mt-0.5 transition-colors ', trackClasses]}
	>
		<span class={['w-4 h-4 transition-transform rounded-full bg-white', thumbClasses]}></span>
	</span>
	<span class="grid">
		{#if children}{@render children()}{:else}
			{#if text}
				<span class="select-none">{text}</span>
			{/if}
		{/if}
		{#if helperTextProps?.text}
			<HelperText {...helperTextProps} />
		{/if}
	</span>
</label>
